import { _breakP } from '../common/js/lib/utils.js';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * GuidanceCont Controller
 *
 * 【目的】
 * guidance セクション内の各 `.js-guidanceCont` に対し、scroll-fade-bg と同様の演出を実装する。
 * - スクロール進捗に応じて 1 枚目のビジュアル（`.js-guidanceVisual` の 0 番目）を
 *   フェードアウトし、2 枚目を現す。
 * - コンテナが画面に入ったら、タイトル（`.js-guidanceTtl` 内の `.t`）を 1 文字ずつ
 *   順番に `is-visible` 付与、最後に subTtl（`.js-guidanceSubTtl`）へ `is-visible` 付与。
 *
 * 【必須 DOM】
 * - `.js-guidanceCont` … セクションルート（複数対応）
 * - `.js-guidanceBgWrap` … 背景ラッパー（PC で ScrollTrigger に pin 対象）
 * - `.js-guidanceVisual` … ビジュアル（0 番目が fade 対象）
 *
 * 【任意 DOM】
 * - `.js-guidanceTtl` と内部の `.t` … 文字ごとの表示演出
 * - `.js-guidanceSubTtl` … タイトル表示後に is-visible
 *
 * 【ブレークポイント】
 * - `utils._breakP.SP`（max-width: 767px）を SP、それ以外を PC として扱う。
 *
 * 【PC】
 * - GSAP ScrollTrigger で `bgWrap` を pin、`onUpdate` で進捗に応じ firstBg の opacity を制御。
 * - progress > `progressTriggerThreshold` でタイトルアニメーションを発火（1 回のみ）。
 *
 * 【SP】
 * - GSAP は一切ロードしない（iOS の慣性スクロール阻害を回避）。
 * - `.js-guidanceBgWrap` は CSS 側の `position: sticky` を前提とする（JS では制御しない）。
 * - `IntersectionObserver` で `__bgWrap` を観察し、画面に完全に収まった瞬間（threshold）に
 *   タイトルアニメーションを 1 回だけ発火。
 * - `scroll` で進捗を算出し firstBg の opacity を更新。
 *
 * 【破棄】
 * - `destroy()` で ScrollTrigger / Observer / scroll listener を解除。
 */
export default class GuidanceCont {
  constructor() {
    this.$guidanceConts = document.querySelectorAll('.js-guidanceCont');
    if (this.$guidanceConts.length === 0) return;

    this.scrollTriggers = [];
    this.observers = [];
    this.scrollHandler = null;
    this.containerData = [];

    this.charDelayMs = 80;
    this.subTtlDelayMs = 400;
    this.progressTriggerThreshold = 0.4;
    // SP: __bgWrap が画面に完全に収まったタイミングで発火。
    // 1.0 ちょうどは浮動小数点の丸めで取りこぼす可能性があるため 0.99 を採用。
    this.observerThreshold = 0.99;
    this.spRevealStartDelayMs = 200;

    this.init();
  }

  init() {
    if (_breakP.SP) {
      this.initSP();
    } else {
      this.initPC();
    }
  }

  /**
   * SP 実装（CSS sticky + IntersectionObserver）
   */
  initSP() {
    this.$guidanceConts.forEach($container => {
      const refs = this.collectRefs($container);
      if (!refs) return;

      // SP の progress 計算は __spScrollWrap（高さ 200vh）を基準にして、
      // __bgWrap が sticky で貼り付いている 100vh ぶんのスクロールで 0 → 1 にする。
      const $scrollRange =
        $container.querySelector('.js-guidanceSpScrollWrap') || $container;

      this.containerData.push({
        $scrollRange,
        $firstBg: refs.$firstBg
      });

      this.setupSpObserver(refs);
    });

    this.setupSpScrollHandler();
  }

  /**
   * SP: タイトル発火用の IntersectionObserver を生成して登録。
   * `__bgWrap`（height: 100vh）をターゲットにし、ビューポートに完全に
   * 収まった瞬間（= sticky で画面を覆った瞬間）に 1 回だけ発火する。
   */
  setupSpObserver(refs) {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.revealTtl(
              refs.$chars,
              refs.$subTtl,
              this.spRevealStartDelayMs
            );
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: this.observerThreshold }
    );

    observer.observe(refs.$bgWrap);
    this.observers.push(observer);
  }

  /**
   * SP: スクロール連動で firstBg の opacity を更新するハンドラーを登録。
   */
  setupSpScrollHandler() {
    let ticking = false;
    this.scrollHandler = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const viewportHeight = window.innerHeight;
        this.containerData.forEach(data => {
          this.updateSpFade(data, viewportHeight);
        });
        ticking = false;
      });
    };

    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  /**
   * SP: 単一コンテナの進捗を算出し、firstBg の opacity を適用する。
   */
  updateSpFade({ $scrollRange, $firstBg }, viewportHeight) {
    const rect = $scrollRange.getBoundingClientRect();
    const rangeHeight = $scrollRange.offsetHeight;

    if (rect.top >= viewportHeight || rect.bottom <= 0) return;

    const scrolled = Math.max(0, -rect.top);
    const scrollableDistance = rangeHeight - viewportHeight;
    const progress =
      scrollableDistance > 0
        ? Math.min(1, Math.max(0, scrolled / scrollableDistance))
        : 0;

    $firstBg.style.opacity = 1 - progress;
  }

  /**
   * PC 実装（GSAP ScrollTrigger）
   */
  initPC() {
    this.$guidanceConts.forEach($container => {
      const refs = this.collectRefs($container);
      if (!refs) return;

      let ttlAnimated = false;

      // 1) pin: __body が伸びても余白が出ないよう、container の末尾まで bgWrap を固定する。
      const pinTrigger = ScrollTrigger.create({
        trigger: $container,
        start: 'top top',
        end: 'bottom bottom',
        pin: refs.$bgWrap,
        pinSpacing: false,
        pinType: 'transform',
        invalidateOnRefresh: true
      });

      // 2) fade / title 発火: SP の __spScrollWrap と揃え、100vh ぶんのスクロールで 0 → 1。
      const fadeTrigger = ScrollTrigger.create({
        trigger: $container,
        start: 'top top',
        end: '+=100%',
        invalidateOnRefresh: true,
        onUpdate: self => {
          gsap.set(refs.$firstBg, { opacity: 1 - self.progress });

          if (!ttlAnimated && self.progress > this.progressTriggerThreshold) {
            ttlAnimated = true;
            this.revealTtl(refs.$chars, refs.$subTtl);
          }
        }
      });

      this.scrollTriggers.push(pinTrigger, fadeTrigger);
    });
  }

  /**
   * コンテナ内の参照要素をまとめて取得
   */
  collectRefs($container) {
    const $bgWrap = $container.querySelector('.js-guidanceBgWrap');
    const $visuals = $container.querySelectorAll('.js-guidanceVisual');
    const $firstBg = $visuals[0] || null;
    const $ttl = $container.querySelector('.js-guidanceTtl');
    const $chars = $ttl ? $ttl.querySelectorAll('.t') : null;
    const $subTtl = $container.querySelector('.js-guidanceSubTtl');

    if (!$bgWrap || !$firstBg) return null;

    return { $bgWrap, $firstBg, $chars, $subTtl };
  }

  /**
   * タイトルの 1 文字ずつに `is-visible` を付与し、末尾で subTtl にも付与。
   *
   * @param {NodeListOf<Element>|null} $chars - 文字要素
   * @param {Element|null} $subTtl - サブタイトル要素
   * @param {number} [startDelayMs=0] - 発火直後に全体を遅らせる初期ディレイ
   */
  revealTtl($chars, $subTtl, startDelayMs = 0) {
    const base = startDelayMs;
    if ($chars && $chars.length > 0) {
      $chars.forEach(($char, i) => {
        setTimeout(
          () => {
            $char.classList.add('is-visible');
          },
          base + i * this.charDelayMs
        );
      });
      const totalDuration =
        base + $chars.length * this.charDelayMs + this.subTtlDelayMs;
      setTimeout(() => {
        if ($subTtl) $subTtl.classList.add('is-visible');
      }, totalDuration);
    } else if ($subTtl) {
      setTimeout(() => {
        $subTtl.classList.add('is-visible');
      }, base);
    }
  }

  /**
   * ScrollTrigger の位置を再計算（PC のみ）
   */
  refresh() {
    if (!_breakP.SP) {
      ScrollTrigger.refresh();
    }
  }

  /**
   * クリーンアップ
   */
  destroy() {
    this.scrollTriggers.forEach(t => t.kill());
    this.scrollTriggers = [];

    this.observers.forEach(o => o.disconnect());
    this.observers = [];

    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }

    this.containerData = [];
  }
}
